import React, { Fragment, useMemo } from 'react';
import { FormatOptionLabelMeta } from 'react-select';

import { emptySelectValue, FormMeta, FormValues, PatientFormMode } from 'components/intake/types';
import { PreferredOptionLabel, PreferredProviderBadge } from 'components/preferredProviders/PreferredProviderLabels';
import usePreferredForGroupId from 'components/preferredProviders/usePreferredForGroupId';
import usePreferredSections from 'components/preferredProviders/usePreferredSections';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import ToggleGroup, { ToggleOption } from 'components/shared/form/ToggleGroup';
import { useForm } from 'context/form';
import { useAddressOptions } from 'hooks/useAddressOptions';
import Group from 'models/Group';
import Profile from 'models/Profile';
import { useInfiniteGroups } from 'services/api/group';

export type RehabFacilityInputGroupProps = {
  profile: Profile;
};

function RehabFacilityInputGroup({ profile }: RehabFacilityInputGroupProps) {
  const { values, meta, errors, setValue } = useForm<FormValues, FormMeta>();
  const owner = values.owner;

  // Sorted by the "Managed By" choice's preferred list, so changing the owner re-sorts.
  const preferredForGroupId = usePreferredForGroupId(owner?.id, values.locationType?.value);
  const groupParams = {
    clientIdOrNetworkClientId: owner?.clientId,
    type: values.locationType?.value,
    include: 'group_type',
    sortBy: 'name asc',
    preferredForGroupId,
  };
  const asyncGroupOptions = useInfiniteGroups(groupParams, { enabled: !!preferredForGroupId });
  const { hasPreferred, isPreferred, sectionProps } = usePreferredSections(asyncGroupOptions.options, groupParams);

  const { formatOptionLabel: formatAddressOption, ...addressProps } = useAddressOptions();
  // In the menu and in the field. The field's provider gets the ribbon when it's on the current "Managed By"'s
  // list, which the early first page covers unless that list has more than a page of this type.
  const formatOptionLabel = (option: Group, meta: FormatOptionLabelMeta<Group>) => (
    <PreferredOptionLabel label={formatAddressOption(option, meta)} preferred={isPreferred(option)} />
  );

  const patientInQueue = !meta.currentRehabState || meta.currentRehabState.queue;
  const editingNonQueuePatient = meta.mode == PatientFormMode.Edit && !patientInQueue;
  const locationTypeIsDisabled = !owner?.id || editingNonQueuePatient;

  const locationTypeOptions = useMemo(() => {
    let enabledProviderTypes = profile.enabledProviderTypes;
    if (!meta.isManagerUser) {
      const ownerClientEnabledProviderTypes = owner?.client?.enabledProviderTypes.map(
        (enabledProviderType) => enabledProviderType.apiName
      );

      enabledProviderTypes = profile.enabledProviderTypes.filter((userProviderType) =>
        ownerClientEnabledProviderTypes?.includes(userProviderType.apiName)
      );
    }

    return enabledProviderTypes.map((groupType) => ({ label: groupType.displayName, value: groupType.apiName }));
  }, [meta.isManagerUser, owner?.client?.enabledProviderTypes, profile.enabledProviderTypes]);

  const hasSingleProviderType = locationTypeOptions.length === 1;

  const isProviderTypeVisible = patientInQueue && locationTypeOptions.length > 1;
  const isProviderTypeDisabled =
    locationTypeIsDisabled || !!meta.patientHasActiveServiceRefusals || !!meta.latestLocationEpisodeIsArchived;

  const isRehabFacilityVisible = !!values.locationType || hasSingleProviderType;

  const groupLabel = values.locationType?.label ? values.locationType.label : locationTypeOptions[0]?.label;

  const handleLocationTypeChange = (change: ToggleOption) => {
    const previousLocationType = values.locationType?.value;

    setValue('locationType', change);

    if (change.value != previousLocationType) {
      setValue('group', null, { validate: false });
      setValue('caseManager', null, { validate: false });
      setValue('utilizationManager', null, { validate: false });
    }
  };

  return (
    <Fragment>
      <InputGroup data-cy='providerType' title='What care is the patient receiving?' visible={isProviderTypeVisible}>
        <ToggleGroup
          selectedOption={values.locationType ?? emptySelectValue}
          disabled={isProviderTypeDisabled}
          options={locationTypeOptions}
          getOptionValue={(prop) => prop.value}
          getOptionLabel={(prop) => prop.label}
          onChange={handleLocationTypeChange}
          autoSelectFirstOption={hasSingleProviderType}
        />
      </InputGroup>
      <InputGroup
        title={groupLabel}
        error={errors.group}
        data-cy='rehabFacility'
        visible={isRehabFacilityVisible}
        badge={hasPreferred && <PreferredProviderBadge />}>
        <Select<Group>
          {...asyncGroupOptions}
          {...sectionProps}
          {...addressProps}
          formatOptionLabel={formatOptionLabel}
          name={groupLabel}
          placeholder={groupLabel}
          getOptionValue={(prop) => prop.id}
          getOptionLabel={(prop) => prop.name}
          onChange={(change) => setValue('group', change)}
          disabled={isProviderTypeDisabled}
          value={values.group}
          hasError={!!errors.group}
        />
      </InputGroup>
    </Fragment>
  );
}

export default RehabFacilityInputGroup;
